import React, {useState, useEffect} from 'react'
import {HashRouter as Router, Routes, Route, Navigate, useNavigate} from 'react-router-dom'
import {supabase} from './config/supabase'
import AuthForm from './components/Auth/AuthForm'
import SignupForm from './components/Auth/SignupForm'
import ForgotPassword from './components/Auth/ForgotPassword'
import Dashboard from './components/Dashboard/Dashboard'
import SettingsPage from './components/Settings/SettingsPage'
import SupportScripts from './components/SupportScripts'
import './App.css'

// Theme context
export const ThemeContext = React.createContext({
  theme: 'light',
  toggleTheme: () => {}
})

// Wrapper component to handle logout with navigation
const LogoutHandler = ({onLogout}) => {
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    try {
      await onLogout();
      // Navigate to login page after logout
      navigate('/login');
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  return <Dashboard user={null} onLogout={handleLogout} />;
};

function App() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [authError, setAuthError] = useState(null)
  const [theme, setTheme] = useState(() => {
    // Get theme from localStorage or default to 'light'
    return localStorage.getItem('theme') || 'light'
  })

  // Apply theme to document
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    // Save theme to localStorage
    localStorage.setItem('theme', theme)
  }, [theme])

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light')
  }

  useEffect(() => {
    // Check if user is already logged in
    const getInitialSession = async () => {
      try {
        const {data: {session}, error} = await supabase.auth.getSession()
        if (error) {
          console.error("Error fetching session:", error)
          setAuthError(error.message)
        }
        setUser(session?.user ?? null)
        setLoading(false)
      } catch (err) {
        console.error("Failed to get session:", err)
        setAuthError("Failed to authenticate. Please try again.")
        setLoading(false)
      }
    }
    
    getInitialSession()
    
    // Listen for auth changes with error handling
    const {data: {subscription}} = supabase.auth.onAuthStateChange(
      async (event, session) => {
        try {
          console.log("Auth state change:", event)
          if (event === 'TOKEN_REFRESHED') {
            return
          }
          setUser(session?.user ?? null)
        } catch (err) {
          console.error("Auth state change error:", err)
        }
      }
    )
    
    // Listen for custom search events
    const handleStartSearch = (event) => {
      const searchKeyword = event.detail.keyword;
      if (searchKeyword) {
        console.log("Starting search for:", searchKeyword);
        // You would trigger your search function here
        // For example: searchFunction(searchKeyword);
      }
    };
    
    document.addEventListener('startSearch', handleStartSearch);
    
    return () => {
      subscription?.unsubscribe();
      document.removeEventListener('startSearch', handleStartSearch);
    }
  }, [])

  // Manual logout function with error handling
  const handleLogout = async () => {
    try {
      setLoading(true)
      await supabase.auth.signOut()
      setUser(null)
      return true
    } catch (error) {
      console.error("Error signing out:", error)
      setAuthError("Failed to sign out. Please try again.")
      return false
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D59A4C]"></div>
      </div>
    )
  }

  return (
    <ThemeContext.Provider value={{theme, toggleTheme}}>
      <Router>
        <div className="App dark:bg-gray-900">
          {authError && (
            <div 
              className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 dark:bg-red-900 dark:text-red-100 dark:border-red-600" 
              role="alert"
            >
              <p>{authError}</p>
              <button 
                onClick={() => setAuthError(null)} 
                className="text-red-700 font-bold hover:text-red-900 dark:text-red-100 dark:hover:text-red-200"
              >
                Dismiss
              </button>
            </div>
          )}

          {user && <SupportScripts />}

          <Routes>
            {user ? (
              <>
                <Route path="/dashboard" element={<Dashboard user={user} onLogout={handleLogout} />} />
                <Route path="/settings" element={<SettingsPage user={user} />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </>
            ) : (
              <>
                <Route path="/login" element={<AuthForm onAuthSuccess={setUser} />} />
                <Route path="/signup" element={<SignupForm onAuthSuccess={setUser} />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/" element={<Navigate to="/login" replace />} />
              </>
            )}
          </Routes>
        </div>
      </Router>
    </ThemeContext.Provider>
  )
}

export default App